import { useCallback, useEffect, useState } from 'react'

/**
 * DiceTok's account layer — a real GameCodex account, not an anonymous pool.
 *
 * Talks to Atlas directly from the browser (public, CORS-open API — see
 * atlas/_instructions/accounts-integration.md), the same "grymwyrd UI" pattern
 * every first-party Atlas front-end uses. Tokens live in localStorage: the
 * access token is short-lived (~15min) and the refresh token rotates on every
 * use, which is the documented v1-acceptable posture for a browser-only app
 * with no server-side session of its own.
 *
 * After register/login, `/dicetok/enroll` grants this account the
 * `dicetok.feat.clip` entitlement — a real signup grants access, not the
 * anonymous pool regretsit-style apps use. See netlify/lib for the "why not a
 * fake subscription row" reasoning.
 */

const ATLAS_API_BASE = 'https://atlas.gamecodex.com/api'
const STORAGE_KEY = 'dicetok.session'

export interface Account {
  id: string
  email: string
  name?: string | null
}

export interface Session {
  accessToken: string
  refreshToken: string
  account: Account
}

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

function saveSession(session: Session | null) {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  else localStorage.removeItem(STORAGE_KEY)
}

interface AtlasSessionResponse {
  accessToken: string
  refreshToken: string
  account: Account
}

async function postJson<T>(path: string, body: unknown): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  let res: Response
  try {
    res = await fetch(`${ATLAS_API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return { ok: false, error: 'Could not reach the account service.' }
  }
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null
  if (!res.ok || !data) {
    return { ok: false, error: data?.error ?? 'Something went wrong.' }
  }
  return { ok: true, data: data as T }
}

/** Grant this account DiceTok's posting entitlement. Safe to call repeatedly. */
async function enroll(accessToken: string): Promise<void> {
  await fetch(`${ATLAS_API_BASE}/dicetok/enroll`, {
    method: 'POST',
    headers: { authorization: `Bearer ${accessToken}` },
  }).catch(() => {
    // Best-effort — a failed enroll here just means the first /api/clip call
    // 403s and the user is told to try signing in again. Nothing is lost.
  })
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(() => loadSession())
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    saveSession(session)
  }, [session])

  const register = useCallback(async (email: string, password: string) => {
    setPending(true)
    setError(null)
    const res = await postJson<AtlasSessionResponse>('/accounts/register', { email, password })
    if (!res.ok) {
      setPending(false)
      setError(res.error)
      return false
    }
    await enroll(res.data.accessToken)
    setSession({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken, account: res.data.account })
    setPending(false)
    return true
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setPending(true)
    setError(null)
    const res = await postJson<AtlasSessionResponse>('/accounts/login', { email, password })
    if (!res.ok) {
      setPending(false)
      setError(res.error)
      return false
    }
    await enroll(res.data.accessToken)
    setSession({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken, account: res.data.account })
    setPending(false)
    return true
  }, [])

  const logout = useCallback(() => {
    if (session) {
      fetch(`${ATLAS_API_BASE}/accounts/logout`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      }).catch(() => {})
    }
    setSession(null)
  }, [session])

  /** Refresh the access token, updating (and returning) the new one, or null on failure. */
  const refresh = useCallback(async (): Promise<string | null> => {
    if (!session) return null
    const res = await postJson<AtlasSessionResponse>('/accounts/refresh', { refreshToken: session.refreshToken })
    if (!res.ok) {
      setSession(null)
      return null
    }
    setSession({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken, account: res.data.account })
    return res.data.accessToken
  }, [session])

  return { session, pending, error, register, login, logout, refresh, clearError: () => setError(null) }
}
